<template>
  <ion-page>
    <ion-content class="welcome-content">
      <div class="background-scene">
        <div class="stars"></div>
        
        <!-- Mathematical Equations Background -->
        <div class="math-equations">
          <div class="equation equation-1">∫ f(x)dx = F(x) + C</div>
          <div class="equation equation-2">e^(iπ) + 1 = 0</div>
          <div class="equation equation-3">∇ × E = -∂B/∂t</div>
          <div class="equation equation-4">∑(n=1→∞) 1/n² = π²/6</div>
          <div class="equation equation-5">φ = (1 + √5)/2</div>
          <div class="equation equation-6">∂²u/∂t² = c²∇²u</div>
          <div class="equation equation-7">∫₀^∞ e^(-x²)dx = √π/2</div>
          <div class="equation equation-8">ζ(s) = ∑(n=1→∞) 1/n^s</div>
          <div class="equation equation-9">∇ · E = ρ/ε₀</div>
          <div class="equation equation-10">Γ(n) = (n-1)!</div>
        </div>
      </div>
      
      <div class="app-title-container">
        <h1 class="app-title">Budgeteer</h1>
        <div class="title-subtitle">Share Your Feedback</div>
      </div>

      <div class="login-container" style="margin-top: 0;">
        <div class="login-header" style="margin-bottom: 0; padding-bottom: 0;">
          <h2 class="login-title" style="margin: 0; padding: 0;">Feedback</h2>
          <p class="login-subtitle">Help us improve Budgeteer by sharing your thoughts</p>
        </div>

        <form @submit.prevent="handleSubmit" class="login-form" style="margin-top: 0;">
          <div class="form-fields-stack" style="margin-top: 0; padding-top: 0;">
            <div class="login-input-group">
              <ion-label>Feedback Type</ion-label>
              <select 
                v-model="feedbackType" 
                class="login-custom-input"
                :class="{ 'input-error': typeError }"
                required
                aria-label="Feedback Type"
                @change="clearTypeError"
              >
                <option value="">Select feedback type</option>
                <option value="bug">Bug Report</option>
                <option value="feature">Feature Request</option>
                <option value="improvement">Improvement Suggestion</option>
                <option value="general">General Feedback</option>
              </select>
              <div v-if="typeError" class="input-error-message">
                <ion-icon :icon="alertCircle" size="small"></ion-icon>
                <span>{{ typeError }}</span>
              </div>
            </div>

            <div class="login-input-group">
              <ion-label>Your Feedback</ion-label>
              <textarea 
                v-model="feedbackMessage" 
                placeholder="Please share your feedback, suggestions, or report any issues you've encountered..."
                class="login-custom-input feedback-textarea"
                :class="{ 'input-error': messageError }"
                required
                aria-label="Feedback Message"
                rows="12"
                @input="clearMessageError"
              ></textarea>
              <div v-if="messageError" class="input-error-message">
                <ion-icon :icon="alertCircle" size="small"></ion-icon>
                <span>{{ messageError }}</span>
              </div>
            </div>
          </div>

          <div class="login-form-actions">
            <ion-button 
              type="submit" 
              expand="block" 
              class="login-button"
              :class="{ 'loading': isLoading }"
              :disabled="isLoading"
              aria-label="Submit Feedback"
            >
              <div v-if="isLoading" class="loading-spinner">
                <ion-spinner name="crescent"></ion-spinner>
              </div>
              <span v-else>Submit Feedback</span>
            </ion-button>

            <div class="register-link">
              <router-link to="/dashboard" aria-label="Back to Dashboard">
                <ion-icon :icon="arrowBack" class="back-icon"></ion-icon>
                Back to Dashboard
              </router-link>
            </div>
          </div>
        </form>
      </div>

      <!-- Toast Notification -->
      <div v-if="showToast" class="toast-notification" :class="toastType">
        <div class="toast-content">
          <ion-icon :icon="toastIcon" class="toast-icon"></ion-icon>
          <span class="toast-message">{{ toastMessage }}</span>
        </div>
        <ion-button fill="clear" class="toast-close" @click="hideToast">
          <ion-icon :icon="close"></ion-icon>
        </ion-button>
      </div>
    </ion-content>
  </ion-page>
</template>

<script>
import { IonPage, IonContent, IonLabel, IonButton, IonIcon, IonSpinner } from '@ionic/vue';
import { alertCircle, close, checkmark, arrowBack } from 'ionicons/icons';
import axios from 'axios';
import { getAPIURL } from '../logic/api';

export default {
  name: 'FeedbackPage',
  components: {
    IonPage,
    IonContent,
    IonLabel,
    IonButton,
    IonIcon,
    IonSpinner
  },
  data() {
    return {
      feedbackType: '',
      feedbackMessage: '',
      isLoading: false,
      typeError: '',
      messageError: '',
      alertCircle,
      close,
      checkmark,
      arrowBack,
      showToast: false,
      toastMessage: '',
      toastType: 'error',
      toastIcon: alertCircle
    }
  },
  methods: {
    showSuccessToast(message) {
      this.toastMessage = message;
      this.toastType = 'success';
      this.toastIcon = this.checkmark;
      this.showToast = true;
      setTimeout(() => {
        this.hideToast();
      }, 4000);
    },
    showErrorToast(message) {
      this.toastMessage = message;
      this.toastType = 'error';
      this.toastIcon = this.alertCircle;
      this.showToast = true;
      setTimeout(() => {
        this.hideToast();
      }, 5000);
    },
    hideToast() {
      this.showToast = false;
    },
    
    validateForm() {
      let isValid = true;
      
      // Clear previous field errors
      this.clearTypeError();
      this.clearMessageError();
      
      // Validate feedback type
      if (!this.feedbackType.trim()) {
        this.typeError = 'Please select a feedback type';
        isValid = false;
      }
      
      // Validate feedback message
      if (!this.feedbackMessage.trim()) {
        this.messageError = 'Please provide your feedback';
        isValid = false;
      } else if (this.feedbackMessage.trim().length < 10) {
        this.messageError = 'Please provide more detailed feedback (at least 10 characters)';
        isValid = false;
      }
      
      return isValid;
    },
    
    clearTypeError() {
      this.typeError = '';
    },
    
    clearMessageError() {
      this.messageError = '';
    },
    
    async handleSubmit() {
      // Validate form first
      if (!this.validateForm()) {
        return;
      }
      
      this.isLoading = true;
      this.clearTypeError();
      this.clearMessageError();
      
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          this.showErrorToast('Please log in to submit feedback');
          this.$router.push('/login');
          return;
        }

        const response = await axios.post(getAPIURL() + '/api/feedback/submit-feedback', {
          type: this.feedbackType,
          message: this.feedbackMessage.trim()
        }, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        this.showSuccessToast('Thank you for your feedback! We appreciate your input.');
        
        // Clear form after successful submission
        this.feedbackType = '';
        this.feedbackMessage = '';
        
        // Redirect to dashboard after a short delay
        setTimeout(() => {
          this.$router.push('/dashboard');
        }, 2000);
        
      } catch (error) {
        console.error('Feedback submission failed:', error);
        
        // Handle different types of errors
        if (error.response) {
          // Server responded with error status
          const status = error.response.status;
          const message = error.response.data?.message || 'Failed to submit feedback';
          
          switch (status) {
            case 401:
              this.showErrorToast('Please log in to submit feedback');
              this.$router.push('/login');
              break;
            case 500:
              this.showErrorToast('Server error. Please try again later.');
              break;
            default:
              this.showErrorToast(message);
          }
        } else if (error.request) {
          // Network error
          this.showErrorToast('Network error. Please check your connection and try again.');
        } else {
          // Other error
          this.showErrorToast('An unexpected error occurred. Please try again.');
        }
      } finally {
        this.isLoading = false;
      }
    }
  }
}
</script>

<style scoped>
/* App Title Container - Custom spacing for feedback page */
.app-title-container {
  margin-bottom: 0;
  padding-top: 0;
}

/* Title Subtitle - Custom spacing for feedback page */
.title-subtitle {
  margin-top: 2rem;
  margin-bottom: 0;
}

/* Login Subtitle */
.login-subtitle {
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.9rem;
  margin-top: 0;
  margin-bottom: 0;
  font-weight: 300;
}

/* Feedback Textarea */
.feedback-textarea {
  resize: vertical;
  min-height: 300px;
  font-family: inherit;
  line-height: 1.5;
}

/* Dark theme dropdown styling */
.login-custom-input option {
  background-color: #2a2a2a;
  color: #ffffff;
  padding: 8px;
}

.login-custom-input:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.2);
}

/* Back Link */
.register-link {
  text-align: center;
  margin-top: 1rem;
}

.register-link a {
  color: rgba(255, 255, 255, 0.8);
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  transition: color 0.3s ease;
}

.register-link a:hover {
  color: rgba(255, 255, 255, 1);
}

.back-icon {
  font-size: 1rem;
}
</style>
